import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { PageDropPosition } from '@/routes/features/pages/hooks/usePageReorder'
import { MAX_PAGE_DEPTH } from '@/routes/features/pages/utils/pageTreeUtils'
import { collectDescendantIds, depthOf } from '@/routes/features/pages/utils/docMove'

/**
 * Pure drag-and-drop rules for the drilled-in space sidebar (design 22c).
 * The components only measure the pointer and render; every "where would
 * this land, and is that allowed" decision lives here so it can be tested.
 */

/** Horizontal step between doc levels, in px. */
export const DOC_INDENT_PX = 16
/** Hovering a doc this long outlines and expands it, so you can drop inside. */
export const DOC_DWELL_MS = 500
/** Where the pointer sits inside a doc when a drag comes from outside the tree. */
export const EXTERNAL_GRAB_OFFSET_PX = 24

type DocLike = Pick<PageDocument, 'id' | 'projectId' | 'parentPageId' | 'sortOrder' | 'createdOn' | 'isDeleted'>

/** One doc row as the sidebar shows it, in tree order. */
export interface VisibleDocRow {
  id: string
  depth: number
  parentId: string | null
  /** Live children, shown or not. */
  childCount: number
  /** Has children and is above the sidebar's level limit. */
  canExpand: boolean
  isExpanded: boolean
  /** Children that exist but sit past the sidebar's level limit. */
  hiddenBelow: number
}

export function compareDocs(a: DocLike, b: DocLike): number {
  if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
  return a.createdOn - b.createdOn
}

/** Live children of `parentId` (null = top level), in sidebar order. */
export function childrenOf<T extends DocLike>(docs: ReadonlyArray<T>, parentId: string | null): Array<T> {
  return docs.filter((d) => !d.isDeleted && (d.parentPageId || null) === parentId).sort(compareDocs)
}

/**
 * The rows the tree renders: each root, then — when it's expanded and above
 * `maxLevels` — its children, depth-first.
 */
export function flattenVisibleDocs<T extends DocLike>(
  docs: ReadonlyArray<T>,
  roots: ReadonlyArray<T>,
  expandedIds: ReadonlySet<string>,
  maxLevels: number,
): Array<VisibleDocRow> {
  const rows: Array<VisibleDocRow> = []
  const seen = new Set<string>()
  const visit = (doc: T, depth: number, parentId: string | null) => {
    if (seen.has(doc.id)) return
    seen.add(doc.id)
    const children = childrenOf(docs, doc.id)
    const atLimit = depth + 1 >= maxLevels
    const canExpand = children.length > 0 && !atLimit
    const isExpanded = canExpand && expandedIds.has(doc.id)
    rows.push({
      id: doc.id,
      depth,
      parentId,
      childCount: children.length,
      canExpand,
      isExpanded,
      hiddenBelow: atLimit ? children.length : 0,
    })
    if (isExpanded) for (const child of children) visit(child, depth + 1, doc.id)
  }
  for (const root of roots) visit(root, 0, null)
  return rows
}

/** Every ancestor of `docId`, nearest first — to open the path to the doc you're on. */
export function ancestorIds(docs: ReadonlyArray<DocLike>, docId: string): Array<string> {
  const byId = new Map(docs.map((d) => [d.id, d]))
  const ids: Array<string> = []
  const seen = new Set<string>([docId])
  let parentId = byId.get(docId)?.parentPageId ?? null
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId)
    ids.push(parentId)
    parentId = byId.get(parentId)?.parentPageId ?? null
  }
  return ids
}

/**
 * The level a dropped doc would take, from how far the pointer sits from the
 * row's left edge. `grabOffsetX` is where inside its own row the doc was
 * picked up, so holding still keeps the depth; move right to nest, left to
 * un-nest.
 */
export function depthFromPointer({
  pointerX,
  rowLeft,
  baseIndentPx,
  grabOffsetX,
  indentPx = DOC_INDENT_PX,
}: {
  pointerX: number
  rowLeft: number
  baseIndentPx: number
  grabOffsetX: number
  indentPx?: number
}): number {
  return Math.round((pointerX - grabOffsetX - rowLeft - baseIndentPx) / indentPx)
}

/** Where the pointer is on a row: which half, and whether it's in the middle band. */
export function rowZone(clientY: number, rect: { top: number; height: number }): { edge: 'top' | 'bottom'; middle: boolean } {
  const ratio = rect.height > 0 ? (clientY - rect.top) / rect.height : 0.5
  return { edge: ratio < 0.5 ? 'top' : 'bottom', middle: ratio >= 0.25 && ratio <= 0.75 }
}

/** Where a doc drop lands, and how the sidebar shows it. */
export interface DocDrop {
  /** A line between rows, or an outlined row to drop inside. */
  mode: 'line' | 'inside'
  /** Level the doc lands at (0 = a top-level space doc). */
  depth: number
  /** The doc it lands under; null = top level. */
  parentId: string | null
  /**
   * Where it lands relative to `targetId`, as usePageReorder understands it.
   * A null target means "last at the top level" (the tree was empty).
   */
  targetId: string | null
  position: PageDropPosition
  /** Row the line is drawn against, and on which edge. */
  lineRowId: string | null
  lineEdge: 'top' | 'bottom'
}

export interface DocDropSource {
  id: string
  /** Current parent, when the doc already lives in this tree. */
  parentId: string | null
  /** Whether the doc already lives in this tree (a space doc). */
  inTree: boolean
  /** Levels below the doc (0 for a leaf). */
  height: number
}

function isNoop(
  docs: ReadonlyArray<DocLike>,
  source: DocDropSource,
  parentId: string | null,
  targetId: string | null,
  position: PageDropPosition,
): boolean {
  if (!source.inTree || (source.parentId || null) !== parentId) return false
  const siblings = childrenOf(docs, parentId).map((d) => d.id)
  const index = siblings.indexOf(source.id)
  if (index === -1) return false
  if (position === 'child' || targetId === null) return index === siblings.length - 1
  if (position === 'after') return siblings[index - 1] === targetId
  return siblings[index + 1] === targetId
}

/** Rows with the dragged doc and its shown sub-pages taken out. */
function withoutSubtree(rows: ReadonlyArray<VisibleDocRow>, sourceId: string) {
  const start = rows.findIndex((r) => r.id === sourceId)
  if (start === -1) return { rows: [...rows], sourceAt: -1, subtree: new Set<string>() }
  const depth = rows[start]!.depth
  let end = start + 1
  while (end < rows.length && rows[end]!.depth > depth) end++
  const subtree = new Set(rows.slice(start, end).map((r) => r.id))
  return { rows: [...rows.slice(0, start), ...rows.slice(end)], sourceAt: start, subtree }
}

/**
 * A line drop between two rows. The gap is above or below the hovered row
 * (`edge`); the pointer's level is clamped to what that gap allows — from the
 * row below's level up to one deeper than the row above — and to the depth
 * limit for the dragged doc's own sub-pages.
 */
export function resolveLineDrop({
  docs,
  rows,
  hoveredId,
  edge,
  desiredDepth,
  source,
  maxDepth = MAX_PAGE_DEPTH,
}: {
  docs: ReadonlyArray<DocLike>
  rows: ReadonlyArray<VisibleDocRow>
  hoveredId: string
  edge: 'top' | 'bottom'
  desiredDepth: number
  source: DocDropSource
  maxDepth?: number
}): DocDrop | null {
  const { rows: list, sourceAt, subtree } = withoutSubtree(rows, source.id)
  let above: VisibleDocRow | undefined
  let below: VisibleDocRow | undefined
  if (hoveredId === source.id) {
    // Over its own row: the gap it already sits in.
    above = sourceAt > 0 ? list[sourceAt - 1] : undefined
    below = list[sourceAt]
  } else {
    if (subtree.has(hoveredId)) return null
    const index = list.findIndex((r) => r.id === hoveredId)
    if (index === -1) return null
    above = edge === 'top' ? list[index - 1] : list[index]
    below = edge === 'top' ? list[index] : list[index + 1]
  }

  const deepest = above ? above.depth + 1 : 0
  const shallowest = Math.min(below ? below.depth : 0, deepest)
  const upper = Math.min(deepest, maxDepth - source.height)
  if (upper < shallowest) return null
  const depth = Math.max(shallowest, Math.min(desiredDepth, upper))

  const line = above
    ? { lineRowId: above.id, lineEdge: 'bottom' as const }
    : { lineRowId: below?.id ?? null, lineEdge: 'top' as const }

  let parentId: string | null
  let targetId: string | null
  let position: PageDropPosition
  if (!above) {
    parentId = null
    targetId = below?.id ?? null
    position = 'before'
  } else if (depth === above.depth + 1) {
    parentId = above.id
    if (below && below.parentId === above.id) {
      targetId = below.id
      position = 'before'
    } else {
      targetId = above.id
      position = 'child'
    }
  } else {
    // Walk up from the row above to its ancestor at this level; land after it.
    const byId = new Map(list.map((r) => [r.id, r]))
    let anchor: VisibleDocRow | undefined = above
    while (anchor && anchor.depth > depth) anchor = anchor.parentId ? byId.get(anchor.parentId) : undefined
    if (!anchor) return null
    parentId = anchor.parentId
    targetId = anchor.id
    position = 'after'
  }

  if (isNoop(docs, source, parentId, targetId, position)) return null
  return { mode: 'line', depth, parentId, targetId, position, ...line }
}

/** Dropping inside a doc (after the 500ms hover): it becomes its last sub-page. */
export function resolveInsideDrop({
  docs,
  targetId,
  source,
  maxDepth = MAX_PAGE_DEPTH,
}: {
  docs: ReadonlyArray<DocLike>
  targetId: string
  source: DocDropSource
  maxDepth?: number
}): DocDrop | null {
  if (targetId === source.id) return null
  if (!docs.some((d) => d.id === targetId && !d.isDeleted)) return null
  if (source.inTree && collectDescendantIds(docs, source.id).includes(targetId)) return null
  const depth = depthOf(docs, targetId) + 1
  if (depth + source.height > maxDepth) return null
  if (isNoop(docs, source, targetId, targetId, 'child')) return null
  return { mode: 'inside', depth, parentId: targetId, targetId, position: 'child', lineRowId: null, lineEdge: 'bottom' }
}

/** Dropping below the last doc: last at the top level (a space doc). */
export function resolveEndDrop({
  docs,
  rows,
  source,
}: {
  docs: ReadonlyArray<DocLike>
  rows: ReadonlyArray<VisibleDocRow>
  source: DocDropSource
}): DocDrop | null {
  const { rows: list } = withoutSubtree(rows, source.id)
  const lastRoot = [...list].reverse().find((r) => r.depth === 0)
  const lastRow = list.at(-1)
  const targetId = lastRoot?.id ?? null
  const position: PageDropPosition = targetId ? 'after' : 'before'
  if (isNoop(docs, source, null, targetId, position)) return null
  return {
    mode: 'line',
    depth: 0,
    parentId: null,
    targetId,
    position,
    lineRowId: lastRow?.id ?? null,
    lineEdge: 'bottom',
  }
}

/** A doc can move to a project it doesn't already live in (sub-pages go with it). */
export function canDropDocOnProject(doc: { projectId: string; isDeleted?: boolean } | undefined, projectId: string): boolean {
  return !!doc && !doc.isDeleted && doc.projectId !== projectId
}

/** Projects only reorder among the space's own projects. */
export function canReorderProject(sourceId: string, targetId: string, projectIds: ReadonlySet<string>): boolean {
  return sourceId !== targetId && projectIds.has(sourceId) && projectIds.has(targetId)
}

/**
 * Where a project would land, or null when that's where it already is
 * (just below the row above it, or just above the row below it).
 */
export function resolveProjectDrop(
  orderedIds: ReadonlyArray<string>,
  sourceId: string,
  targetId: string,
  edge: 'top' | 'bottom',
): 'before' | 'after' | null {
  const position = edge === 'top' ? 'before' : 'after'
  const from = orderedIds.indexOf(sourceId)
  const to = orderedIds.indexOf(targetId)
  if (from === -1 || to === -1 || from === to) return null
  if (position === 'after' && to === from - 1) return null
  if (position === 'before' && to === from + 1) return null
  return position
}
